import { useState, useEffect, useRef, useId } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface TrustPanelProps {
  projectId: string
  scoredCount: number
  totalCount: number
  accuracy: number
  missCount: number
  abstainedCount?: number
  defaultOpen?: boolean
}

/**
 * Returns the accuracy summary string for TrustPanel.
 * Exported for unit testing.
 * When abstainedCount > 0: "Scored X of Y — abstained on Z — N% on scored"
 * Otherwise: "Scored X of Y — N% on scored questions"
 */
export function getAccuracyLabel({
  scoredCount,
  totalCount,
  accuracy,
  abstainedCount,
}: {
  scoredCount: number
  totalCount: number
  accuracy: number
  abstainedCount?: number
}): string {
  const pct = Math.round(accuracy * 100)
  if (abstainedCount && abstainedCount > 0) {
    return `Scored ${scoredCount} of ${totalCount} — abstained on ${abstainedCount} — ${pct}% on scored`
  }
  return `Scored ${scoredCount} of ${totalCount} — ${pct}% on scored questions`
}

export function TrustPanel({
  projectId,
  scoredCount,
  totalCount,
  accuracy,
  missCount,
  abstainedCount,
  defaultOpen = false,
}: TrustPanelProps) {
  const storageKey = `echo_trust_panel_opened_${projectId}`
  const panelBodyId = `trust-panel-body-${useId()}`

  // Capture first-ever status once via ref so it's stable across re-renders
  // and survives React Strict Mode's double-invoke of the render function.
  const isFirstEverRef = useRef<boolean>(
    defaultOpen && !localStorage.getItem(storageKey)
  )
  const isFirstEver = isFirstEverRef.current

  const [isOpen, setIsOpen] = useState<boolean>(() => {
    return defaultOpen && !localStorage.getItem(storageKey)
  })

  // Write to localStorage after mount (not inside the initializer) so that
  // Strict Mode double-invocation doesn't set the key before the second render
  // evaluates isFirstEverRef.
  useEffect(() => {
    if (isFirstEverRef.current) localStorage.setItem(storageKey, '1')
  }, [storageKey])

  const trustPanelRef = useRef<HTMLDivElement>(null)

  // Scroll into view on first-ever open
  useEffect(() => {
    if (isOpen && isFirstEver) {
      trustPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [isOpen, isFirstEver])

  return (
    <div ref={trustPanelRef} data-testid="trust-panel">
      {/* Toggle button */}
      <button
        type="button"
        onClick={() => setIsOpen(prev => !prev)}
        aria-expanded={isOpen}
        aria-controls={panelBodyId}
        className="w-full flex justify-between items-center px-4 py-3 text-sm font-medium text-deep-ink hover:bg-soft-panel rounded-xl border border-border-gray"
      >
        <span>Accuracy &amp; methodology</span>
        {isOpen
          ? <ChevronUp className="h-4 w-4 text-slate-400" strokeWidth={1.75} />
          : <ChevronDown className="h-4 w-4 text-slate-400" strokeWidth={1.75} />
        }
      </button>

      {/* Collapsible body */}
      <div
        id={panelBodyId}
        className={cn(
          'transition-all duration-150 overflow-hidden',
          isOpen ? 'block' : 'hidden'
        )}
      >
        <div className="px-4 pb-4 pt-2 space-y-2">
          {/* First-ever explainer — only shown on the first-ever open */}
          {isFirstEver && (
            <p className="text-xs text-echo-indigo/70 italic">
              This is the first result in your project. We opened accuracy details by default — they&apos;re always here when you need them.
            </p>
          )}

          {/* Accuracy line */}
          <p className="text-sm text-deep-ink">
            {getAccuracyLabel({ scoredCount, totalCount, accuracy, abstainedCount })}
          </p>

          {/* Miss count */}
          <p className="text-xs text-slate-500">
            {missCount} prediction{missCount !== 1 ? 's' : ''} missed during backtest
          </p>

          {/* See where we went wrong link — py-2.5 for 40px tap target */}
          <Link
            to={`/app/projects/${projectId}/twin-panel/misses`}
            className="text-xs text-echo-indigo hover:underline py-2.5 inline-block"
          >
            See where we went wrong &rarr;
          </Link>

          {/* One-line explainer */}
          <p className="text-xs text-slate-500">
            Twins are backtested on held-out answers from your survey. Accuracy reflects scored predictions only — abstentions are excluded.
          </p>
        </div>
      </div>

      {/* Regulated-industry disclaimer — always visible outside collapsible */}
      <p className="text-xs text-slate-400 mt-2 px-1">
        Decision support only. Echosome surfaces directional signals from your data. Final decisions stay with your legal, compliance, medical, or regulatory team.
      </p>
    </div>
  )
}
